<template>
  <e-form v-model="isValid" ref="formRef" class="tariff-edit">
    <property-block>
      <template #text>
        <div class="paragraph-l font-600">Общая информация</div>
      </template>
      <template #content>
        <multilanguage-field
          v-model="data.name"
          :rules="[required()]"
          :common-rules="[maxLength(255)]"
          label="Наименование Тарифного плана"
          class="mb-4"
        />
        <e-select
          v-model="data.nds"
          :items="taxSchemesCollection"
          item-title="name"
          item-value="cid"
          :rules="[required()]"
          label="НДС"
          class="tariff-edit__input_thin"
        />
      </template>
    </property-block>

    <divider class="my-6" />

    <property-block>
      <template #text>
        <div class="paragraph-l font-600">Правила применения</div>
      </template>
      <template #content>
        <e-select
          v-model="data.penaltyScheme"
          :items="penaltySchemeCollection"
          :rules="[required()]"
          item-title="name"
          item-value="shortName"
          label="Правила штрафования"
          class="mb-4"
        />
        <e-select
          v-model="data.supplierPayRules"
          :items="supplierPayRulesCollection"
          :rules="[required()]"
          item-title="name"
          item-value="shortName"
          label="Оплата поставщику"
        />
      </template>
    </property-block>

    <property-block class="my-6">
      <template #text>
        <div class="paragraph-l font-600 mb-2">Релиз-период</div>
        <div class="paragraph-s_h text-secondary">
          Укажите период закрытия продаж по данному тарифу до расчетного часа заезда. Например, если заезд в 12:00 и вы
          указали 4 часа, то продажи закроются в 8:00.
        </div>
      </template>
      <template #content>
        <div class="d-flex align-center">
          <e-input
            v-model="data.stopSalePeriod"
            :rules="[required()]"
            :maska="stopSalePeriodMask"
            label="Закрывать продажи за"
            class="tariff-edit__input_thin"
          />
          <div v-if="data.stopSalePeriod" class="input-text ml-4">
            {{ declensionString(data.stopSalePeriod, ['час', 'часа', 'часов']) }}
          </div>
        </div>
      </template>
    </property-block>

    <property-block>
      <template #text>
        <div class="paragraph-l font-600">Расчётные часы</div>
      </template>
      <template #content>
        <div class="d-flex flex-column flex-sm-row">
          <e-time-picker
            v-model="data.time[0]"
            :error-messages="checkInErrorMessage"
            :rules="[required()]"
            label="Расчетный час заезда"
            class="tariff-edit__input_thin mr-4 mb-4"
          />
          <e-time-picker
            v-model="data.time[1]"
            :error-messages="checkOutErrorMessage"
            :rules="[required()]"
            label="Расчетный час выезда"
            class="tariff-edit__input_thin mb-4"
          />
        </div>
      </template>
    </property-block>

    <property-block>
      <template #content>
        <div class="d-flex align-center">
          <e-checkbox v-model="data.checkOutOnNextDay" label="Выезд на следующие сутки" />
          <help-tooltip class="ml-2"
            >Когда установлена галочка "Выезд на следующие сутки", выезд происходит на следующие сутки. При снятой
            галочке заезд и выезд происходят в одни сутки.
          </help-tooltip>
        </div>
      </template>
    </property-block>
  </e-form>
</template>

<script setup lang="ts">
import { ref, toRef } from 'vue';
import type { TariffFormData } from '@/parts/tariffs/types/tariffs';
import Divider from '@/core/components/helpers/Divider.vue';
import PropertyBlock from '@/core/components/interface/PropertyBlock.vue';
import { ECheckbox, EForm, EInput, ESelect, ETimePicker, HelpTooltip, MultilanguageField } from '@/core/components/ui';
import { stopSalePeriodMask } from '@/core/consts/masks';
import { useCollectionsStore } from '@/core/store/collections';
import { getTimeFromPTH } from '@/core/utils/date';
import { declensionString } from '@/core/utils/string';
import { maxLength, required } from '@/core/utils/validation';

const data = defineModel<TariffFormData>({ required: true });

const isValid = ref(false);

const formRef = ref();

const submit = () => {
  formRef.value?.validate();

  if (!isValid.value) {
    return false;
  }

  return {
    name: data.value.name!,
    taxSchemeCid: data.value.nds!,
    applyingRules: {
      penaltySchemeShortName: data.value.penaltyScheme!,
      supplierPayRuleShortName: data.value.supplierPayRules!,
      stopSalePeriod: +data.value.stopSalePeriod!,
    },
    checkInOutSettings: {
      checkInTime: data.value.time[0],
      checkOutTime: data.value.time[1],
      nextDayCheckout: data.value.checkOutOnNextDay,
    },
  };
};

const {
  penaltySchemeCollection,
  getPenaltySchemeCollection,
  supplierPayRulesCollection,
  getSupplierPayRulesCollection,
  taxSchemesCollection,
  getTaxSchemesCollection,
} = useCollectionsStore();

const checkInErrorMessage = toRef(() => {
  if (!data.value.time[0] || !data.value.time[1]) {
    return '';
  }

  return !data.value.checkOutOnNextDay && getTimeFromPTH(data.value.time[1]) === getTimeFromPTH(data.value.time[0])
    ? 'Значения не могут совпадать'
    : !data.value.checkOutOnNextDay && getTimeFromPTH(data.value.time[0]) > getTimeFromPTH(data.value.time[1])
      ? 'Заезд не может быть позже выезда'
      : '';
});

const checkOutErrorMessage = toRef(() => {
  if (!data.value.time[0] || !data.value.time[1]) {
    return '';
  }

  return !data.value.checkOutOnNextDay && getTimeFromPTH(data.value.time[1]) === getTimeFromPTH(data.value.time[0])
    ? 'Значения не могут совпадать'
    : '';
});

defineExpose({
  submit,
});

const promises = [getSupplierPayRulesCollection(), getPenaltySchemeCollection(), getTaxSchemesCollection()];
await Promise.all(promises);

if (!data.value.nds && taxSchemesCollection.value?.[0]) {
  data.value.nds = taxSchemesCollection.value[0].cid;
}

if (!data.value.penaltyScheme && penaltySchemeCollection.value?.length === 1) {
  data.value.penaltyScheme = penaltySchemeCollection.value[0].shortName;
}

if (!data.value.supplierPayRules && supplierPayRulesCollection.value?.length === 1) {
  data.value.supplierPayRules = supplierPayRulesCollection.value[0].shortName;
}
</script>

<style scoped lang="scss">
.tariff-edit {
  &__input {
    &_thin {
      max-width: 280px;
    }
  }
}
</style>
